/**
 * LocalStorage keys used across the enterprise app.
 * Keys are scoped by companyId to preserve preferences when switching companies.
 */

// Key prefixes for pattern matching during cleanup
// Each app section has its own separate key to track the last selected entity independently
const KEY_PREFIXES = {
  GIT: 'git-selected-entity-id',
  COMPLIANCE_MONITOR: 'compliance-monitor-selected-entity-id',
  DOCUMENTS: 'documents-selected-entity-id',
  ACCOUNTING: 'accounting-selected-entity-id', // Separate key for Accounting app
  TAX_AND_REPORTING: 'tax-and-reporting-selected-entity-id', // Separate key for Tax & Reporting app
  TRANSFER_PRICING: 'transfer-pricing-selected-entity-id', // Separate key for Transfer Pricing app
} as const;

// Functions to generate company-scoped keys
export const getGitSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.GIT}-${companyId}`;

export const getComplianceMonitorSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.COMPLIANCE_MONITOR}-${companyId}`;

export const getDocumentsSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.DOCUMENTS}-${companyId}`;

export const getAccountingSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.ACCOUNTING}-${companyId}`;

export const getTaxAndReportingSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.TAX_AND_REPORTING}-${companyId}`;

export const getTransferPricingSelectedEntityKey = (companyId: number) =>
  `${KEY_PREFIXES.TRANSFER_PRICING}-${companyId}`;

export const getTPBenchmarkingVisitedKey = (companyId: number, corporationId: number) =>
  `tp-benchmarking-visited-${companyId}-${corporationId}`;

/**
 * Clears all entity selection keys from localStorage.
 * Used during logout to clean up all company-specific preferences.
 */
export const clearAllEntitySelectionKeys = () => {
  const prefixes = Object.values(KEY_PREFIXES);
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};
